<template>
    <div class="rsvp-action-widget">
        <div v-if="currentState === 'pending'" style="text-align:center;padding:10px 0;">
            <!-- Inline additional fields: display existing + capture empty -->
            <div v-if="hasFields" style="text-align:left;margin-bottom:20px;">
                <p v-if="hasEditableFields" style="margin:0 0 12px;font-size:0.95rem;color:#6b7280;">Please provide the following information:</p>
                <PublicAdditionalFields
                    ref="widgetFields"
                    :fields="additionalFields"
                    :existing-values="existingValues"
                    v-model="fieldValues"
                    theme="light"
                />
            </div>
            <p style="margin:0 0 16px;font-size:1.15rem;font-weight:600;">Will you be attending?</p>
            <button
                :disabled="submitting"
                style="background:#22c55e;color:#fff;padding:14px 48px;border:none;border-radius:8px;font-size:1.05rem;font-weight:700;cursor:pointer;margin:0 8px 8px;transition:all 0.2s;box-shadow:0 4px 15px rgba(34,197,94,0.4);"
                @click="onAccept"
                @mouseover="$event.target.style.transform='translateY(-2px)';$event.target.style.boxShadow='0 6px 20px rgba(34,197,94,0.5)'"
                @mouseout="$event.target.style.transform='none';$event.target.style.boxShadow='0 4px 15px rgba(34,197,94,0.4)'"
            >
                <span v-if="submitting && pendingResponse === 'accepted'">Processing...</span>
                <span v-else>&#10003; Accept</span>
            </button>
            <button
                :disabled="submitting"
                style="background:transparent;color:#ef4444;padding:14px 48px;border:2px solid #ef4444;border-radius:8px;font-size:1.05rem;font-weight:700;cursor:pointer;margin:0 8px 8px;transition:all 0.2s;"
                @click="$emit('respond', 'declined')"
                @mouseover="$event.target.style.background='#ef4444';$event.target.style.color='#fff'"
                @mouseout="$event.target.style.background='transparent';$event.target.style.color='#ef4444'"
            >
                <span v-if="submitting && pendingResponse === 'declined'">Processing...</span>
                <span v-else>&#10005; Decline</span>
            </button>
        </div>

        <div v-else-if="currentState === 'accepted'" style="text-align:center;padding:20px 0;">
            <div style="width:70px;height:70px;background:rgba(34,197,94,0.15);border-radius:50%;margin:0 auto 16px;display:flex;align-items:center;justify-content:center;">
                <span style="font-size:2rem;color:#22c55e;">&#10003;</span>
            </div>
            <h3 style="margin:0 0 8px;font-size:1.6rem;font-weight:800;color:#22c55e;">You're In!</h3>
            <p style="margin:0 0 16px;opacity:0.85;font-size:1rem;">{{ responseResult.message || 'Your RSVP has been confirmed!' }}</p>
            <div v-if="responseResult.rsvp_code" style="background:rgba(59,130,246,0.1);border:2px dashed rgba(59,130,246,0.4);border-radius:12px;padding:20px;display:inline-block;margin:0 0 12px;">
                <p style="margin:0 0 6px;font-size:0.85rem;opacity:0.7;">Your RSVP Code:</p>
                <p style="margin:0;font-size:1.8rem;font-weight:800;color:#3b82f6;letter-spacing:3px;">{{ responseResult.rsvp_code }}</p>
            </div>
            <p v-if="responseResult.rsvp_code" style="margin:0;font-size:0.85rem;opacity:0.6;">Use this code with your email to login at the portal</p>
            <div v-if="responseResult.portal_login_url" style="margin-top:16px;">
                <a :href="responseResult.portal_login_url" style="display:inline-block;background:#3b82f6;color:#fff;padding:12px 32px;border-radius:8px;text-decoration:none;font-weight:600;font-size:0.95rem;">Go to Portal</a>
            </div>
        </div>

        <div v-else-if="currentState === 'declined'" style="text-align:center;padding:20px 0;">
            <div style="width:70px;height:70px;background:rgba(156,163,175,0.15);border-radius:50%;margin:0 auto 16px;display:flex;align-items:center;justify-content:center;">
                <span style="font-size:2rem;color:#9ca3af;">&#9432;</span>
            </div>
            <h3 style="margin:0 0 8px;font-size:1.4rem;font-weight:700;">Thank you for letting us know</h3>
            <p style="margin:0;opacity:0.7;font-size:0.95rem;">If you change your mind, you can use the same link to update your RSVP.</p>
        </div>

        <div v-else-if="currentState === 'already_responded'" style="text-align:center;padding:20px 0;">
            <div style="width:70px;height:70px;background:rgba(59,130,246,0.15);border-radius:50%;margin:0 auto 16px;display:flex;align-items:center;justify-content:center;">
                <span style="font-size:2rem;color:#3b82f6;">&#9432;</span>
            </div>
            <h3 style="margin:0 0 8px;font-size:1.4rem;font-weight:700;">You have already responded</h3>
            <p style="margin:0 0 16px;opacity:0.7;">
                Your RSVP status:
                <span :style="statusBadgeStyle">{{ rsvpStatus }}</span>
            </p>
            <div v-if="hasRsvpEditableFields" style="text-align:left;margin-top:20px;">
                <p style="margin:0 0 12px;font-size:0.9rem;color:#6b7280;"><i class="fa fa-pencil" style="margin-right:4px;"></i> You can update your information below:</p>
                <PublicAdditionalFields
                    ref="widgetEditFields"
                    :fields="additionalFields"
                    :existing-values="existingValues"
                    v-model="fieldValues"
                    theme="light"
                />
                <div style="text-align:center;margin-top:16px;">
                    <button
                        :disabled="updatingFields"
                        style="background:#3b82f6;color:#fff;padding:10px 32px;border:none;border-radius:8px;font-size:0.9rem;font-weight:600;cursor:pointer;transition:all 0.2s;"
                        @click="onUpdateFields"
                    >
                        <span v-if="updatingFields">Saving...</span>
                        <span v-else>Update Information</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PublicAdditionalFields from '@/components/EventInvitations/PublicAdditionalFields.vue'

export default {
    name: 'RSVPActionWidget',
    components: { PublicAdditionalFields },
    props: {
        responseState: { type: String, default: 'pending' },
        submitting: { type: Boolean, default: false },
        pendingResponse: { type: String, default: null },
        responseResult: { type: Object, default: () => ({}) },
        rsvpStatus: { type: String, default: '' },
        additionalFields: { type: Array, default: () => [] },
        existingValues: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            fieldValues: {},
            updatingFields: false,
        }
    },
    computed: {
        hasFields() {
            return this.additionalFields.length > 0
        },
        hasEditableFields() {
            if (!this.hasFields) return false
            const existing = this.existingValues
            return this.additionalFields.some(f => {
                if (f.editable_on_rsvp) return true
                const val = existing[f.field_name]
                return val === undefined || val === null || val === ''
            })
        },
        hasRsvpEditableFields() {
            if (!this.hasFields) return false
            return this.additionalFields.some(f => f.editable_on_rsvp)
        },
        currentState() {
            return this.responseState
        },
        statusBadgeStyle() {
            const colors = {
                accepted: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e' },
                declined: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
                sent: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6' },
                pending: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
            }
            const c = colors[this.rsvpStatus] || colors.pending
            return `display:inline-block;padding:4px 14px;border-radius:20px;background:${c.bg};color:${c.color};font-weight:700;font-size:0.85rem;text-transform:capitalize;`
        },
    },
    watch: {},
    methods: {
        onAccept() {
            if (this.hasEditableFields) {
                const isValid = this.$refs.widgetFields?.validate()
                if (!isValid) return
                this.$emit('respond', {
                    response: 'accepted',
                    additional_fields: { ...this.fieldValues },
                })
            } else {
                this.$emit('respond', 'accepted')
            }
        },
        onUpdateFields() {
            if (this.$refs.widgetEditFields) {
                const isValid = this.$refs.widgetEditFields.validate()
                if (!isValid) return
            }
            this.$emit('update-fields', { ...this.fieldValues })
        },
    },
}
</script>
